import React from 'react';
import PropTypes from 'prop-types';
import { CSSTransition } from 'react-transition-group';
import cx from 'classnames';

const sizes = {
    xs: 16,
    sm: 24,
    md: 32,
    lg: 48,
    xl: 64
};

export default class Spinner extends React.PureComponent {
    static propTypes = {
        /** Spinner's display size */
        size: PropTypes.oneOfType([
            PropTypes.oneOf(['xs', 'sm', 'md', 'lg', 'xl']),
            PropTypes.number
        ]),

        /** If true, has display flex and justify-content center. If false, has
        display inline-flex */
        isCentered: PropTypes.bool,

        /** Sets spinner visibility */
        isSpinning: PropTypes.bool,

        /** Callback fired after spinner has completed the exit animation. */
        onComplete: PropTypes.func,

        /** Changes the color of the spinner */
        color: PropTypes.oneOf(['default', 'inverted', 'brand', 'subtle', 'danger']),

        /** Description of the spinner read by screen readers. */
        screenReaderText: PropTypes.string
    };

    static defaultProps = {
        size: 'xl',
        isCentered: true,
        isSpinning: true,
        color: 'default',
        screenReaderText: 'Content is loading',
        onComplete: undefined
    };

    onExited = () => {
        const { onComplete } = this.props;

        if (onComplete) {
            onComplete();
        }
    }

    getCircleStrokeWidth = () => {
        const { size } = this.props;
        const computedSize = typeof size === 'number' ? size : sizes[size];

        if (computedSize >= 80) {
            return 4 / (computedSize / 80);
        }

        if (computedSize >= 64) {
            return 4 / (computedSize / 64);
        }

        if (computedSize >= 48) {
            return 4.5 / (computedSize / 48);
        }

        if (computedSize >= 32) {
            return 5.5 / (computedSize / 32);
        }

        if (computedSize >= 24) {
            return 6 / (computedSize / 24);
        }

        return 6.5;
    }

    render() {
        const {
            isSpinning,
            screenReaderText,
            size,
            isCentered,
            color
        } = this.props;
        const computedSize = typeof size === 'number' ? size : sizes[size];

        return (
            <div
                data-component="Spinner"
                className={cx('sarsa-spinner-outer-container', {
                    'is-centered': isCentered
                })}>
                <CSSTransition
                    classNames="sarsa-spinner"
                    appear
                    in={isSpinning}
                    mountOnEnter
                    unmountOnExit
                    timeout={{
                        enter: 600,
                        exit: 300
                    }}
                    onExited={this.onExited}>
                    <div
                        className="sarsa-spinner-inner-container"
                        style={{ width: `${computedSize}px`, height: `${computedSize}px`}} >
                        <svg
                            className={cx("sarsa-spinner-svg", {
                                'color-is-inverted': color === 'inverted',
                                'color-is-brand': color === 'brand',
                                'color-is-danger': color === 'danger',
                                'color-is-subtle': color === 'subtle'
                            })}
                            aria-hidden="true"
                            viewBox="25 25 50 50">
                                <circle
                                    strokeWidth={this.getCircleStrokeWidth()}
                                    strokeMiterlimit={this.getCircleStrokeWidth() * 2}
                                    cx="50"
                                    cy="50"
                                    r="20" />
                        </svg>
                        <span className="rec-sr-only">{screenReaderText}</span>
                    </div>
                </CSSTransition>
            </div>
        );
    }
}
